import { useState, useEffect, useRef, useMemo } from "react";
import {
  Badge,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import moment from "moment";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";

const getBatchStatusColor = (status) => {
  switch (status) {
    case "COMPLETED":
      return "green";
    case "PENDING":
    default:
      return "yellow";
  }
};

const getGroupStatusColor = (status) => {
  switch (status) {
    case "CONFIRM":
      return "green";
    case "PENDING":
    default:
      return "yellow";
  }
};

const getProviderGroupForDn = (settlement, dnId) => {
  if (!settlement?.settlements || !dnId) return null;

  return settlement.settlements.find((group) =>
    (group.debitNotes ?? []).some((note) => {
      const noteId = note?._id ?? note;
      return noteId?.toString?.() === dnId.toString();
    }),
  );
};

const getDebitNoteInGroup = (group, dnId) => {
  if (!group?.debitNotes || !dnId) return null;

  return group.debitNotes.find((note) => {
    const noteId = note?._id ?? note;
    return noteId?.toString?.() === dnId.toString();
  });
};

const buildSettlementViews = (settlements, dnId, dn) =>
  settlements
    .map((settlement) => {
      const group = getProviderGroupForDn(settlement, dnId);
      const dnInBatch = getDebitNoteInGroup(group, dnId);
      if (!group || !dnInBatch) return null;

      const isCurrentBatch =
        dn?.giSettlement &&
        (dn.giSettlement._id ?? dn.giSettlement)?.toString?.() ===
          settlement._id?.toString?.();

      return {
        _id: settlement._id,
        code: settlement.code || "-",
        batchStatus: settlement.status || "-",
        groupStatus: group.status || "-",
        settleDate: settlement.settleDate,
        paymentMethod: settlement.paymentMethod || "-",
        dnCostTotal: dnInBatch.costTotal ?? dn?.costTotal,
        dnPaymentStatus: dnInBatch.paymentStatus ?? dn?.paymentStatus,
        dnSettleAmount: isCurrentBatch ? dn?.settlement?.amount : null,
        dnSettledAt: isCurrentBatch ? dn?.settledAt : null,
      };
    })
    .filter(Boolean);

const SettlementRowTable = ({ view, formatter }) => (
  <Table fontSize="xs" striped highlightOnHover withBorder>
    <thead>
      <tr>
        <th>Group Status</th>
        <th>Settle Date</th>
        <th>Payment Method</th>
        <th>DN Status</th>
        <th style={{ textAlign: "right" }}>DN Cost</th>
        <th style={{ textAlign: "right" }}>Settled Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>
          <Badge size="xs" color={getGroupStatusColor(view.groupStatus)}>
            {view.groupStatus}
          </Badge>
        </td>
        <td>
          <Text size="xs">
            {view.settleDate
              ? moment(view.settleDate).format("YYYY-MM-DD")
              : "-"}
          </Text>
        </td>
        <td>
          <Text size="xs">{view.paymentMethod || "-"}</Text>
        </td>
        <td>
          {view.dnPaymentStatus ? (
            <Badge
              size="xs"
              color={view.dnPaymentStatus === "SETTLED" ? "green" : "gray"}
            >
              {view.dnPaymentStatus}
            </Badge>
          ) : (
            <Text size="xs">-</Text>
          )}
        </td>
        <td style={{ textAlign: "right" }}>
          <Text size="xs">
            {view.dnCostTotal != null
              ? formatter.currency(view.dnCostTotal)
              : "-"}
          </Text>
        </td>
        <td style={{ textAlign: "right" }}>
          <Stack spacing={0}>
            <Text size="xs">
              {view.dnSettleAmount != null
                ? formatter.currency(view.dnSettleAmount)
                : "-"}
            </Text>
            {view.dnSettledAt && (
              <Text size="xs" color="dimmed">
                {moment(view.dnSettledAt).format("YYYY-MM-DD HH:mm")}
              </Text>
            )}
          </Stack>
        </td>
      </tr>
    </tbody>
  </Table>
);

const DebitNoteInsurerSettlementSection = ({ form }) => {
  const dn = form?.values;
  const debitNoteId = dn?._id?.toString?.() ?? dn?._id;
  const giSettlementId = dn?.giSettlement?._id ?? dn?.giSettlement;

  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    if (!debitNoteId) {
      setSettlements([]);
      return;
    }

    let cancelled = false;

    const loadSettlements = async () => {
      setLoading(true);
      try {
        const result = await apiRef.current.search({
          apiEntity: "giSettlement",
          pageSize: 50,
          sort: { by: "createdAt", order: "desc" },
          searchQuery: { "settlements.debitNotes": debitNoteId },
          byAggregation: false,
        });

        let docs = result?.docs ?? [];

        const hasLinkedBatch = giSettlementId
          ? docs.some(
              (row) =>
                row._id?.toString?.() === giSettlementId?.toString?.(),
            )
          : false;

        if (giSettlementId && !hasLinkedBatch) {
          const linked = await apiRef.current.getById({
            apiEntity: "giSettlement",
            id: giSettlementId,
          });
          if (linked) {
            docs = [linked, ...docs];
          }
        }

        if (!cancelled) setSettlements(docs);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching insurer settlements:", error);
          setSettlements([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSettlements();

    return () => {
      cancelled = true;
    };
  }, [debitNoteId, giSettlementId]);

  const settlementViews = useMemo(
    () => buildSettlementViews(settlements, debitNoteId, dn),
    [settlements, debitNoteId, dn],
  );

  if (!dn) {
    return (
      <Group position="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!debitNoteId) {
    return (
      <Text size="sm" color="dimmed">
        Save the debit note to view insurer settlement.
      </Text>
    );
  }

  if (loading) {
    return (
      <Group position="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack spacing="md">
      {settlementViews.length === 0 ? (
        <Text size="sm" color="dimmed">
          No insurer settlement batch contains this debit note.
        </Text>
      ) : (
        settlementViews.map((view) => (
          <Stack key={view._id} spacing="xs">
            <Group spacing="sm">
              <Title order={6}>{view.code}</Title>
              <Badge size="sm" color={getBatchStatusColor(view.batchStatus)}>
                {view.batchStatus}
              </Badge>
            </Group>
            <SettlementRowTable view={view} formatter={formatter} />
          </Stack>
        ))
      )}
    </Stack>
  );
};

export default DebitNoteInsurerSettlementSection;
